import moment from "moment";
import API from "../services/api.service";
import { APIS } from "../constants/apis";

// The app calendar's palette, taken from calendar.dart / app_constants.dart so
// the two stay in step. The app colours the DATE, not the cell — see main.scss.
//   Peak      orange  #FFA500   (khakiColor() resolves to AppConstants.orange)
//   High Peak crimson #932A24
//   Maint.    red     #E83737
//   Booked    no colour of its own — struck through, like the app
export const DAY_TYPE_COLORS = {
  peak: "#FFA500",
  high_peak: "#932A24",
  maintenance: "#E83737",
  booked: "rgba(8, 32, 23, 0.45)",
};

// Fill for a maintenance BAR on the dashboard calendar. The strong red above is
// right for a date number or a legend swatch, but as a filled span it swamps a
// calendar whose booking bars are all pale villa tints. This is that same red
// blended 85% toward white, which lands at the villa bars' own lightness
// (~0.90) so the row reads evenly; the date number carries the strong colour.
export const MAINTENANCE_BAR = "#FCE1E1";

export type SpecialDayIndex = {
  exact: Record<string, number>; // YYYY-MM-DD -> holiday type (2 peak, 3 high peak)
  highPeakWeekend: Set<string>; // YYYY-MM-DD pulled in by an adjacent High Peak
};

export const EMPTY_SPECIAL_DAYS: SpecialDayIndex = {
  exact: {},
  highPeakWeekend: new Set<string>(),
};

// Special days are global (not per-villa), so this is fetched once and reused
// for every villa's picker.
//
// Only STORED rows count. A recurring Peak is materialised per-year through
// Settings > Special Days, so its month/day is deliberately NOT projected into
// other years — the API stopped doing that too (see "Stop synthesizing
// recurring peak dates"). Projecting it painted seasons in years no admin had
// added, e.g. a 2028-2030 summer season colouring 2032, and made deletions look
// like they had not stuck.
export async function fetchSpecialDays(): Promise<SpecialDayIndex> {
  try {
    const res = await API.get(APIS.SPECIAL_DAYS.LIST);
    const rows: any[] = res.data?.data || [];
    const exact: Record<string, number> = {};
    // A High Peak day drags its long weekend in with it. The API does this for
    // the app's calendar (booking.service.ts getWeekendDates, applied to
    // highpeak_date only): a FRIDAY takes the Saturday and Sunday after it, a
    // MONDAY takes the Saturday and Sunday before it, and any other weekday
    // stands alone. Peak (type 2) is never expanded. Without mirroring it here
    // the panel painted only the stored Friday crimson while the app painted
    // all three days of the same weekend.
    const highPeakWeekend = new Set<string>();
    rows.forEach((r: any) => {
      const day = moment(r.date).format("YYYY-MM-DD");
      const type = Number(r.type);
      // if a date somehow carries more than one row, the strongest wins
      exact[day] = Math.max(exact[day] || 0, type);
      if (type === 3) {
        const d = moment(r.date);
        const iso = d.isoWeekday();
        if (iso === 5) {
          highPeakWeekend.add(d.clone().add(1, "days").format("YYYY-MM-DD"));
          highPeakWeekend.add(d.clone().add(2, "days").format("YYYY-MM-DD"));
        } else if (iso === 1) {
          highPeakWeekend.add(
            d.clone().subtract(1, "days").format("YYYY-MM-DD")
          );
          highPeakWeekend.add(
            d.clone().subtract(2, "days").format("YYYY-MM-DD")
          );
        }
      }
    });
    return { exact, highPeakWeekend };
  } catch (e) {
    // A missing legend must never break the picker it sits under.
    return EMPTY_SPECIAL_DAYS;
  }
}

// ---------------------------------------------------------------------------
// Per-villa day types, straight from booking-calendar-propertyid/:id.
//
// Prefer this over the global index above wherever a villa is known. The API
// has already applied BOTH rules the app's calendar obeys, so reading its
// output means the panel cannot drift from the app:
//   * the long weekend  — a Friday High Peak pulls in Sat+Sun, a Monday the
//     Sat+Sun before it (getWeekendDates)
//   * the 14-day decay  — inside 14 days a High Peak day survives only if THIS
//     villa is already booked on it, otherwise it is an ordinary day
// The second is per-villa and booking-dependent, so it cannot be derived from
// the global special-days list at all.
// ---------------------------------------------------------------------------
export type VillaDayIndex = {
  peak: Set<string>; // YYYY-MM-DD
  highPeak: Set<string>; // YYYY-MM-DD
};

export const EMPTY_VILLA_DAYS: VillaDayIndex = {
  peak: new Set<string>(),
  highPeak: new Set<string>(),
};

// The API formats these as `YYYY-MM-DD HH:MM:SS.SSS`, where MM is the MONTH
// token and SS is not a real one — so everything after the date is noise. Only
// the leading YYYY-MM-DD is meaningful, which is why every caller slices 10.
const toDay = (s: any) => String(s).slice(0, 10);

export function villaDayIndex(data: any): VillaDayIndex {
  return {
    peak: new Set<string>((data?.peak_date || []).map(toDay)),
    highPeak: new Set<string>((data?.highpeak_date || []).map(toDay)),
  };
}

// High Peak outranks Peak, the order calendar.dart paints them in.
export function villaDayClass(current: any, idx: VillaDayIndex): string {
  const day = current.format("YYYY-MM-DD");
  if (idx.highPeak.has(day)) return "cell_high_peak";
  if (idx.peak.has(day)) return "cell_peak";
  return "";
}

// Cell class for a date, or "" when it is an ordinary general day — so the
// result can be dropped straight into a className.
export function specialDayClass(current: any, idx: SpecialDayIndex): string {
  const day = current.format("YYYY-MM-DD");
  const type = idx.exact[day];
  // High Peak outranks Peak, matching the order the app applies its styles in
  // (calendar.dart paints peak first, then lets high peak overwrite it), so a
  // Saturday stored as Peak still reads crimson when the Friday beside it is
  // High Peak.
  if (type === 3 || idx.highPeakWeekend.has(day)) return "cell_high_peak";
  if (type === 2) return "cell_peak";
  return "";
}

export function specialDayLabel(cls: string): string {
  if (cls === "cell_high_peak") return "High Peak day";
  if (cls === "cell_peak") return "Peak day";
  return "";
}
